import { type PrettyStyleHelpers, prettyStyleHelpers } from './styleHelpers';
import { isPretty } from './typeguards';
import type { LogCSSStyle, Pretty, PrettyFn, PrettyPart } from './types';

export interface PrettyBuilderFrom {
    (): this;
    (value: string, style?: LogCSSStyle | true, ...styles: LogCSSStyle[]): this;
    (value: number, style?: LogCSSStyle | true, ...styles: LogCSSStyle[]): this;
    (
        value: number,
        precision: number,
        style?: LogCSSStyle | true,
        ...styles: LogCSSStyle[]
    ): this;
    (value: Pretty, forceObject?: boolean): this;
    (value: any, forceObject?: boolean): this;
}

export interface PrettyBuilder extends _PrettyBuilder, PrettyBuilderFrom {}

export interface PrettyBuilderConstructor
    extends PrettyBuilderFrom,
        Iterable<PrettyPart>,
        Pretty,
        PrettyStyleHelpers {
    new (parts?: Iterable<PrettyPart>): PrettyBuilder;
    create(
        from?: Pretty | (($: PrettyBuilderConstructor) => Iterable<PrettyPart>)
    ): PrettyBuilder;
    c(style?: LogCSSStyle): PrettyBuilder;
    s(
        value: string,
        style?: LogCSSStyle | true,
        ...styles: LogCSSStyle[]
    ): PrettyBuilder;
    i(
        value: number,
        style?: LogCSSStyle | true,
        ...styles: LogCSSStyle[]
    ): PrettyBuilder;
    d(
        value: number,
        style?: LogCSSStyle | true,
        ...styles: LogCSSStyle[]
    ): PrettyBuilder;
    f(
        value: number,
        precision?: number,
        style?: LogCSSStyle | true,
        ...styles: LogCSSStyle[]
    ): PrettyBuilder;
    o(value: any): PrettyBuilder;
    O(value: any): PrettyBuilder;
    p(
        value: Pretty | (($: PrettyBuilderConstructor) => Iterable<PrettyPart>)
    ): PrettyBuilder;
}

class _PrettyBuilder extends Function implements Iterable<PrettyPart>, Pretty {
    protected parts: PrettyPart[];
    readonly __isPrettyBuilder = true as const;

    public constructor(parts: Iterable<PrettyPart> = []) {
        super();
        this.parts = [...parts];
        return new Proxy(this, {
            apply: (target, thisArg, argArray: [any?, any?, any?]) =>
                target.from(...argArray),
        });
    }

    public static create(
        from?: Pretty | (($: PrettyBuilderConstructor) => Iterable<PrettyPart>)
    ): PrettyBuilder {
        const res = new _PrettyBuilder() as unknown as PrettyBuilder;
        if (!from) return res;
        return res.p(from);
    }

    public push(...parts: PrettyPart[]) {
        this.parts.push(...parts);
        return this;
    }

    public append(parts: Iterable<PrettyPart>) {
        this.parts.push(...parts);
        return this;
    }

    public prepend(parts: Iterable<PrettyPart>) {
        this.parts.unshift(...parts);
        return this;
    }

    public c(style?: LogCSSStyle, ...styles: LogCSSStyle[]) {
        return this.push({ specifier: 'c', style, styles });
    }
    public s(
        value: string,
        style?: LogCSSStyle | true,
        ...styles: LogCSSStyle[]
    ) {
        return this.push({ specifier: 's', value, style, styles });
    }
    public i(
        value: number,
        style?: LogCSSStyle | true,
        ...styles: LogCSSStyle[]
    ) {
        return this.push({ specifier: 'i', value, style, styles });
    }
    public d(
        value: number,
        style?: LogCSSStyle | true,
        ...styles: LogCSSStyle[]
    ) {
        return this.push({ specifier: 'd', value, style, styles });
    }
    public f(
        value: number,
        precision?: number,
        style: LogCSSStyle | true = true,
        ...styles: LogCSSStyle[]
    ) {
        return this.push({ specifier: 'f', value, precision, style, styles });
    }
    public o(value: any) {
        return this.push({ specifier: 'o', value });
    }
    public O(value: any) {
        return this.push({ specifier: 'O', value });
    }
    public p(
        value: Pretty | (($: PrettyBuilderConstructor) => Iterable<PrettyPart>)
    ) {
        if (isPretty(value)) {
            return this.append(value.__pretty(PrettyBuilder));
        } else if (typeof value === 'function') {
            return this.append(value(PrettyBuilder));
        } else {
            return this;
        }
    }

    public from(): this;
    public from(
        value: string,
        style?: LogCSSStyle | true,
        ...styles: LogCSSStyle[]
    ): this;
    public from(
        value: number,
        style?: LogCSSStyle | true,
        ...styles: LogCSSStyle[]
    ): this;
    public from(
        value: number,
        precision: number,
        style?: LogCSSStyle | true,
        ...styles: LogCSSStyle[]
    ): this;
    public from(value: Pretty, forceObject?: boolean): this;
    public from(value: any, forceObject?: boolean): this;
    public from(...args: any[]): this {
        if (args.length === 0) return this.c();
        const value = args[0];
        args = args.slice(1);
        if (typeof value === 'string') return this.s(value, ...args);
        if (typeof value === 'number') {
            if (typeof args[0] === 'number') {
                return this.f(value, ...args);
            } else {
                return this.i(value, ...args);
            }
        }
        if (args[0]) return this.O(value);
        if (isPretty(value)) return this.p(value);
        return this.o(value);
    }

    __pretty($: PrettyBuilderConstructor) {
        return new $(this);
    }

    [Symbol.iterator]() {
        return this.parts[Symbol.iterator]();
    }
}

const __pretty: PrettyFn = ($) =>
    $(
        'PrettyBuilder',
        $.fg('purple', 'dark'),
        $.bg('purple', 'light'),
        $.mono,
        $.bold
    );

export const PrettyBuilder = Object.assign(
    function (...args: [any?, any?, any?]) {
        const res = _PrettyBuilder.create();
        if (!new.target) return res.from(...args);
        return res.append(args[0]);
    },
    {
        create: _PrettyBuilder.create,
        c: (...args: any[]) => (_PrettyBuilder.create() as any).c(...args),
        s: (...args: any[]) => (_PrettyBuilder.create() as any).s(...args),
        i: (...args: any[]) => (_PrettyBuilder.create() as any).i(...args),
        d: (...args: any[]) => (_PrettyBuilder.create() as any).d(...args),
        f: (...args: any[]) => (_PrettyBuilder.create() as any).f(...args),
        o: (...args: any[]) => (_PrettyBuilder.create() as any).o(...args),
        O: (...args: any[]) => (_PrettyBuilder.create() as any).O(...args),
        p: (...args: any[]) => (_PrettyBuilder.create() as any).p(...args),
        __pretty,
        [Symbol.iterator]: () => __pretty(PrettyBuilder)[Symbol.iterator](),
        ...prettyStyleHelpers,
    }
) as unknown as PrettyBuilderConstructor;
